import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'elements/Button';
import { useCountdown } from 'hooks/useCountdown';
import { isRateLimitError } from 'networking/mindfeed/utils/apiClient';
import classNames from 'classnames';

const OTP_LENGTH = 6;
// The form only mounts right after a code was sent, so the cooldown starts
// immediately and restarts after every successful resend. In-memory only: a
// reload resets it.
const RESEND_COOLDOWN_SECONDS = 60;

interface Props {
  email: string;
  isVerifying: boolean;
  isSending: boolean;
  onBack: () => void;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
}

const OtpForm: React.FC<Props> = ({
  email,
  isVerifying,
  isSending,
  onBack,
  onSubmit,
  onResend,
}) => {
  const t = useTranslations();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { secondsLeft, start: startCooldown } = useCountdown(
    RESEND_COOLDOWN_SECONDS,
    { autoStart: true },
  );

  const code = digits.join('');
  const canResend = !isSending && secondsLeft === 0;

  const handleSubmit = async () => {
    if (code.length < OTP_LENGTH) return;
    setError(null);
    try {
      await onSubmit(code);
    } catch (err) {
      if (isRateLimitError(err)) {
        setError(
          err.minutesUntilRetry
            ? t('auth.otp.error.rateLimited', { count: err.minutesUntilRetry })
            : t('auth.otp.error.rateLimitedNoRetry'),
        );
      } else {
        setError(t('auth.otp.error.invalid'));
      }
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setDigits(Array(OTP_LENGTH).fill(''));
    setError(null);
    await onResend();
    startCooldown();
  };

  const resendLabel = isSending
    ? t('auth.otp.sending')
    : secondsLeft > 0
      ? t('auth.otp.resendIn', { seconds: secondsLeft })
      : t('auth.otp.resend');

  const onDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const onDigitKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const onDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div className={styles.container}>
      <button type="button" onClick={onBack} className={styles.backButton}>
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        {t('common.back')}
      </button>
      <div className={styles.header}>
        <p className={styles.title}>{t('auth.otp.title')}</p>
        <p className={styles.subtitle}>
          {t.rich('auth.otp.subtitle', {
            email,
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
      </div>
      <div className={styles.otp.wrapper}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            autoFocus={i === 0}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => onDigitChange(i, e.target.value)}
            onKeyDown={(e) => onDigitKeyDown(i, e)}
            onPaste={onDigitPaste}
            className={styles.otp.input}
          />
        ))}
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <Button
        type="button"
        variant="solid"
        color="neutral"
        size="md"
        fullWidth
        loading={isVerifying}
        loadingText={t('auth.otp.verifying')}
        className="text-sm font-semibold"
        onClick={handleSubmit}
        disabled={code.length < OTP_LENGTH}
      >
        {t('common.continue')}
      </Button>
      <button
        type="button"
        onClick={handleResend}
        disabled={!canResend}
        className={styles.link}
      >
        {resendLabel}
      </button>
    </div>
  );
};

export default OtpForm;

const styles = {
  container: 'flex flex-col w-72 space-y-4 text-left',
  header: 'space-y-4',
  title: 'text-lg font-semibold text-mf-off-black-950',
  subtitle: 'text-sm text-mf-off-black-500',
  backButton: classNames(
    'self-start flex items-center gap-1.5 text-sm text-mf-gray-500 hover:text-mf-gray-700',
    'transition-colors duration-150',
  ),
  otp: {
    wrapper: 'flex gap-2 justify-center',
    input: classNames(
      'w-10 h-12 text-center text-lg font-semibold rounded-xl border border-mf-gray-200',
      'bg-white focus:outline-none focus:border-mf-blue-500 focus:ring-1 focus:ring-mf-blue-500',
    ),
  },
  error: 'text-sm text-red-500 text-center',
  link: classNames(
    'text-sm text-mf-off-black-600 underline',
    'disabled:text-mf-gray-400 disabled:cursor-not-allowed',
  ),
};
